import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/mutations"

import {
  UpdateTransferPalletLpnsDocument,
  type UpdateTransferPalletLpnsFailure,
  type UpdateTransferPalletLpnsInput,
  type UpdateTransferPalletLpnsMutation,
  type UpdateTransferPalletLpnsMutationVariables,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateTransferPalletLpnsReturn {
  updateTransferPalletLpns: (input: UpdateTransferPalletLpnsInput) => Promise<boolean>
  isUpdating: boolean
}

export const useUpdateTransferPalletLpns = (): UseUpdateTransferPalletLpnsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    UpdateTransferPalletLpnsMutation,
    UpdateTransferPalletLpnsMutationVariables
  >(UpdateTransferPalletLpnsDocument)

  const updateTransferPalletLpns = useCallback(
    async (input: UpdateTransferPalletLpnsInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.transfer.updateTransferPalletLpns
      const hasError = error || output?.__typename === "UpdateTransferPalletLpnsFailure"

      if (hasError) {
        const message = error?.message ?? (output as UpdateTransferPalletLpnsFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      notify({ type: NotificationType.Success, message: "Transfer pallet LPNs updated" })
      return true
    },
    [executeMutation, notify]
  )

  return { updateTransferPalletLpns, isUpdating: fetching }
}
